import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { slugify } from '@/entities/section-picker';
import { notificationService } from '@/lib/services/notification.service';
import {
  fetchLatestTemplates,
  TEMPLATES_QUERY_KEYS,
  templatesQueryOptions,
  upsertTemplates,
} from './templates.api';
import type { TemplateItem } from './templates.types';
import { useTemplatesScope } from './useEmailTemplates';

/** Thrown when a create would collide with an existing slug. Carries the name for the message. */
export class DuplicateTemplateSlugError extends Error {
  constructor(readonly templateName: string) {
    super(`Template "${templateName}" already exists`);
    this.name = 'DuplicateTemplateSlugError';
  }
}

/** Thrown when a name contains nothing `slugify` keeps, so it would yield an empty route param. */
export class EmptyTemplateSlugError extends Error {
  constructor(readonly templateName: string) {
    super(`Template "${templateName}" has no usable slug`);
    this.name = 'EmptyTemplateSlugError';
  }
}

/**
 * Thrown when a write targets a slug the server no longer has — it was deleted by another session
 * while this screen was open. Without it the recipe's `map` quietly matches nothing, the unchanged
 * array is written back, and the user is told their edit was saved.
 */
export class TemplateNotFoundError extends Error {
  constructor(readonly slug: string) {
    super(`Template "${slug}" no longer exists`);
    this.name = 'TemplateNotFoundError';
  }
}

type Recipe = (current: TemplateItem[]) => TemplateItem[];

interface OptimisticContext {
  previous: TemplateItem[] | undefined;
}

function stamp(template: TemplateItem): TemplateItem {
  return { ...template, updatedAt: new Date().toISOString() };
}

/**
 * Write path for the templates blob.
 *
 * All templates live in ONE tool-context-data record, so every mutation is read-modify-write over
 * the full array: read the latest from the server, splice by slug, upsert everything back. Writing
 * from local state instead would drop edits another session made to a DIFFERENT template.
 *
 * List mutations are optimistic — the list has no Save button, so a row must react instantly and a
 * failed write must visibly restore the previous list rather than leave a phantom row behind.
 */
export function useEmailTemplateMutations() {
  const { t } = useTranslation('admin');
  const scope = useTemplatesScope();
  const queryClient = useQueryClient();
  const listKey = templatesQueryOptions(scope).queryKey;

  const applyOptimistic = useCallback(
    async (recipe: Recipe): Promise<OptimisticContext> => {
      await queryClient.cancelQueries({ queryKey: listKey });
      const previous = queryClient.getQueryData<TemplateItem[]>(listKey);
      if (previous) {
        queryClient.setQueryData<TemplateItem[]>(listKey, recipe(previous));
      }
      return { previous };
    },
    [queryClient, listKey],
  );

  const rollback = useCallback(
    (context: OptimisticContext | undefined, message: string) => {
      if (context?.previous) {
        queryClient.setQueryData<TemplateItem[]>(listKey, context.previous);
      }
      notificationService.showError(message);
    },
    [queryClient, listKey],
  );

  const settle = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEYS.all });
    // The chat composer's template dropdown reads its own key; without this it keeps serving a
    // stale list after a create / rename / delete made here.
    void queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEYS.chatConsumers });
  }, [queryClient]);

  const write = useCallback(
    async (recipe: Recipe) => {
      const current = await fetchLatestTemplates(scope);
      const next = recipe(current);
      await upsertTemplates(scope, next);
      return next;
    },
    [scope],
  );

  /**
   * Seed the cache with what was actually persisted, so consumers see the new truth without
   * waiting for the invalidation refetch. The editor depends on this: its draft/saved sync adopts
   * `savedHtml` the moment the draft goes clean, and without the seed that still holds the
   * PRE-save HTML — so every save visibly reverted the editor for a full round-trip.
   */
  const seed = useCallback(
    (next: TemplateItem[]) => queryClient.setQueryData<TemplateItem[]>(listKey, next),
    [queryClient, listKey],
  );

  /**
   * Serialises every write to this org/workspace's templates record. All templates share ONE
   * row, so each mutation is read-modify-write over the whole blob — and query-core runs
   * scope-less mutations concurrently, which let a second mutation read before the first had
   * written and silently undo it. Keyed per tenant so unrelated workspaces don't queue behind
   * each other.
   */
  const mutationScope = useMemo(
    () => ({ id: `templates-blob:${scope.organizationId}:${scope.workspaceId ?? 'org'}` }),
    [scope.organizationId, scope.workspaceId],
  );

  /**
   * Map over the blob, failing loudly when the slug is gone. `Array.map` on its own matches
   * nothing and hands back an identical array, which upserts cleanly and reports success — so the
   * user is told an edit landed on a template that no longer exists.
   */
  const mapExisting = useCallback(
    (slug: string, update: (template: TemplateItem) => TemplateItem): Recipe =>
      (current) => {
        if (!current.some((template) => template.slug === slug)) {
          throw new TemplateNotFoundError(slug);
        }
        return current.map((template) => (template.slug === slug ? update(template) : template));
      },
    [],
  );

  const createTemplate = useMutation({
    mutationFn: async (templateName: string) => {
      const slug = slugify(templateName);
      // Defensive: the modal blocks this, but an empty slug would persist a row whose route
      // param is empty — permanently unopenable, and colliding with the next such name.
      if (!slug) throw new EmptyTemplateSlugError(templateName);
      const current = await fetchLatestTemplates(scope);
      if (current.some((template) => template.slug === slug)) {
        throw new DuplicateTemplateSlugError(templateName);
      }
      await upsertTemplates(scope, [...current, stamp({ templateName, slug, html: '' })]);
      return slug;
    },
    onMutate: (templateName) =>
      applyOptimistic((current) => {
        // Painting a row the mutation is about to reject gives the table two rows with the same
        // slug, and rows are keyed by slug. The modal blocks this case up front; this keeps the
        // cross-session race from producing a duplicate key on its way to the rollback.
        const slug = slugify(templateName);
        if (current.some((template) => template.slug === slug)) return current;
        return [...current, stamp({ templateName, slug, html: '' })];
      }),
    onError: (error, _templateName, context) =>
      rollback(
        context,
        error instanceof DuplicateTemplateSlugError
          ? t('templatesConfig.mutations.templateAlreadyExists', {
              name: error.templateName,
            })
          : error instanceof EmptyTemplateSlugError
            ? t('templatesConfig.nameModal.needsLatinCharacters')
            : t('templatesConfig.mutations.failedToCreateTemplate'),
      ),
    onSettled: settle,
    scope: mutationScope,
  });

  const renameTemplate = useMutation({
    // Renaming NEVER re-derives the slug: it is the agent's template key and this screen's route
    // param, so re-slugging would orphan agent references and break existing links.
    mutationFn: ({ slug, templateName }: { slug: string; templateName: string }) =>
      write(mapExisting(slug, (template) => stamp({ ...template, templateName }))),
    onMutate: ({ slug, templateName }: { slug: string; templateName: string }) =>
      applyOptimistic((current) =>
        current.map((template) =>
          template.slug === slug ? stamp({ ...template, templateName }) : template,
        ),
      ),
    onSuccess: seed,
    onError: (_error, _variables, context) =>
      rollback(context, t('templatesConfig.mutations.failedToRenameTemplate')),
    onSettled: settle,
    scope: mutationScope,
  });

  const deleteTemplate = useMutation({
    mutationFn: (slug: string) =>
      write((current) => current.filter((template) => template.slug !== slug)),
    onMutate: (slug: string) =>
      applyOptimistic((current) => current.filter((template) => template.slug !== slug)),
    onSuccess: seed,
    onError: (_error, _slug, context) =>
      rollback(context, t('templatesConfig.mutations.failedToDeleteTemplate')),
    onSettled: settle,
    scope: mutationScope,
  });

  const saveTemplateHtml = useMutation({
    // Not optimistic: the editor owns the draft locally until Save, so there is nothing to
    // roll back — the draft simply stays dirty when the write fails.
    mutationFn: ({ slug, html }: { slug: string; html: string }) =>
      write(mapExisting(slug, (template) => stamp({ ...template, html }))),
    onSuccess: (next) => {
      seed(next);
      notificationService.showSuccess(t('templatesConfig.mutations.templateSaved'));
    },
    onError: (error) => {
      notificationService.showError(
        error instanceof TemplateNotFoundError
          ? t('templatesConfig.mutations.templateNoLongerExists')
          : t('templatesConfig.mutations.failedToSaveTemplate'),
      );
    },
    onSettled: settle,
    scope: mutationScope,
  });

  const saveComplementaryPrompt = useMutation({
    // Not optimistic, for the same reason as `saveTemplateHtml`: the sheet owns its draft until its
    // own Save, so a failed write leaves that draft in place rather than needing a rollback.
    mutationFn: ({ slug, complementaryPrompt }: { slug: string; complementaryPrompt: string }) =>
      write(mapExisting(slug, (template) => stamp({ ...template, complementaryPrompt }))),
    onSuccess: (next) => {
      seed(next);
      notificationService.showSuccess(t('templatesConfig.mutations.customInstructionsSaved'));
    },
    onError: (error) => {
      notificationService.showError(
        error instanceof TemplateNotFoundError
          ? t('templatesConfig.mutations.templateNoLongerExists')
          : t('templatesConfig.mutations.failedToSaveCustomInstructions'),
      );
    },
    onSettled: settle,
    scope: mutationScope,
  });

  return {
    createTemplate,
    renameTemplate,
    deleteTemplate,
    saveTemplateHtml,
    saveComplementaryPrompt,
  };
}
